import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { verifyDomainForApplePay } from '../../utils';
import CheckoutSettings from '../types/CheckoutSettings';
import Coupon from '../types/Coupon';
import Member from '../types/Member';
import Price from '../types/Price';
import Site from '../types/Site';

// This handles setting all the form values that are used by the different checkout components and
// inputs.
const useCheckout = (): void => {
  const { setValue, watch } = useFormContext();

  // The specific checkout hook should set these after fetching the data from the server for the
  // checkout. Then this can use them to setup the checkout configuration as expected.
  const price: Price | undefined = watch('price');
  const member: Member | undefined = watch('member');
  const site: Site | undefined = watch('site');
  const checkoutSettings: CheckoutSettings | undefined = watch('checkoutSettings');
  const applePayDomainVerified: boolean | undefined = watch('applePayDomainVerified');

  // set static settings and default dynamic values -- static settings don't change unless you are
  // reloading an entirely new checkout page. These should come from the member, site, or checkout
  // settings data that's loaded from the server (and represents the configuration for this
  // checkout).
  useEffect(() => {
    if (price && checkoutSettings) {
      if (checkoutSettings.stripePublishableKey && checkoutSettings.stripeAccountId) {
        loadStripe(checkoutSettings.stripePublishableKey, {
          betas: ['elements_enable_deferred_intent_beta_1'],
          stripeAccount: checkoutSettings.stripeAccountId,
        }).then((stripe: Stripe | null) => setValue('stripe', stripe));
      }

      setValue('currency', price.currency);
      setValue('addressRequired', checkoutSettings.addressRequired);
      setValue('nameRequired', checkoutSettings.nameRequired);
      setValue('phoneRequired', checkoutSettings.phoneNumberRequired);
      setValue('paymentMethods', checkoutSettings.paymentMethods);
      setValue('additionalPaymentMethods', checkoutSettings.additionalPaymentMethods);
      setValue('vendorAccountId', checkoutSettings.vendorAccountId);
      setValue('checkoutPageColor', checkoutSettings.checkoutPageColor);
      setValue('checkoutTextOverride', checkoutSettings.checkoutTextOverride);
      setValue('customFields', checkoutSettings.customFields);
      setValue('applePayDomainVerified', false);

      // default dynamic values
      // 'address' stores all parts of an address -- see AddressInput
      setValue('address', { subdivisionRequired: false });
      setValue('amountPriceInCents', 0);
      setValue('attemptReloadWithCardsOnly', false);
      setValue('coupon', undefined);
      setValue('customFieldValues', undefined);
      setValue('dueNowPriceInCents', price?.amount || 0);
      setValue('email', undefined);
      setValue('isFree', false);
      setValue('isPaymentDetailComplete', false);
      setValue('isPaymentInProgress', false);
      setValue('name', undefined);
      setValue('paymentProvider', 'card');
      setValue('phone', undefined);
      setValue('saveCard', site?.cardStorageOptinPreference === 'opt_out');
      setValue('saveCardDefaultValue', false);
      setValue('showSaveCardCheckbox', false);
      setValue('useSavedCard', !!member?.savedCardDetails);
    }
  }, [price, member, site, checkoutSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // set dynamic values that change as the customer interacts with the
  const coupon: Coupon | undefined = watch('coupon');
  const dueNowPriceInCents: number | undefined = watch('dueNowPriceInCents');
  const paymentProvider: string | undefined = watch('paymentProvider');
  const useSavedCard: boolean | undefined = watch('useSavedCard');
  const showSaveCardCheckbox: boolean | undefined = watch('showSaveCardCheckbox');

  useEffect(() => {
    if (!price) {
      return;
    }
    // TODO: add order bump, tax, coupon, and quantity handling here -- see popup checkout logic in
    // usePopupCheckout
    setValue('dueNowPriceInCents', price.amount);
  }, [price]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // TODO: add order bump handling -- see popup checkout logic in usePopupCheckout
    setValue('amountPriceInCents', dueNowPriceInCents);
  }, [dueNowPriceInCents]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // A gift coupon makes the offer free
    if (coupon) {
      setValue('isFree', coupon.giftCoupon);
    } else if (price && price.subscriptionPlanAttributes) {
      // For a subscription offer, $0 payments + $0 setup fee means free.
      const { paymentAmount, setupFee } = price.subscriptionPlanAttributes;
      setValue('isFree', paymentAmount + setupFee === 0);
    } else {
      // For a non-subscription offer, $0 due now means free.
      setValue('isFree', dueNowPriceInCents === 0);
    }
  }, [price, dueNowPriceInCents, coupon]);

  useEffect(() => {
    const value =
      !useSavedCard &&
      price?.priceType !== 'recurring' &&
      site?.cardStorageOptinPreference &&
      ['opt_in', 'opt_out'].includes(site.cardStorageOptinPreference) &&
      paymentProvider !== 'external_paypal';
    setValue('showSaveCardCheckbox', value);
  }, [useSavedCard, price, site, paymentProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const value = !showSaveCardCheckbox && site?.cardStorageOptinPreference === 'opt_in';
    setValue('saveCardDefaultValue', value);
  }, [showSaveCardCheckbox, site]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // skip if no ApplePay session exists
    if (!window.ApplePaySession) return;
    // skip if ApplePay is not enabled on the Offer
    if (!site || !checkoutSettings?.additionalPaymentMethods?.includes('apple_pay')) return;
    // skip if the domain has already been verified
    if (applePayDomainVerified) return;

    verifyDomainForApplePay(site).then(async () => {
      setValue('applePayDomainVerified', true);
    });
  }, [site, checkoutSettings]);
};

export default useCheckout;
